import React, { useState, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight, CheckCircle, XCircle } from 'lucide-react';
import "../../css/teacher/Attendance.css";
import Loader from '../../components/Loader';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function Attendance() {
    const [isLoading, setIsLoading] = useState(true);
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const [attendanceData, setAttendanceData] = useState([]);
    const [yearStats, setYearStats] = useState([]);
    const [activeView, setActiveView] = useState('month');
    const [error, setError] = useState(null);

    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const formatTime = (date) => {
        if (!date) return "--";

        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;
        return `${hours}:${minutes} ${ampm}`;
    };

    // Fetch teacher's monthly attendance
    const fetchTeacherAttendance = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            setError("Unauthorized! Please log in again.");
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(`https://schoolpro-api.uniums.com/api/teacher/attendance/my?month=${currentMonth + 1}&year=${currentYear}`, {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            // Check if response is JSON
            const contentType = response.headers.get("content-type");
            if (!contentType || !contentType.includes("application/json")) {
                throw new Error("Server returned non-JSON response");
            }

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch attendance data");
            }

            const result = await response.json();
            if (!result.success) {
                throw new Error(result.message || "Failed to fetch attendance data");
            }

            const processedData = result.data.map(record => {
                const recordDate = new Date(record.date);
                const entryTime = new Date(record.entryTime);

                const currentDate = new Date();
                const recordDateOnly = new Date(recordDate.getFullYear(), recordDate.getMonth(), recordDate.getDate());
                const currentDateOnly = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());

                const fourPM = new Date(recordDate);
                fourPM.setHours(16, 0, 0, 0);

                const showCheckoutTime =
                    (recordDateOnly.getTime() === currentDateOnly.getTime() && currentDate >= fourPM) ||
                    (recordDateOnly < currentDateOnly);


                return {
                    date: recordDate,
                    status: record.status,
                    checkInTime: formatTime(entryTime),
                    checkOutTime: showCheckoutTime ? '4:00 PM' : '--'
                };
            });

            setAttendanceData(processedData);
        } catch (error) {
            console.error("Error fetching attendance:", error);
            toast.error(`Error: ${error.message}`);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchYearlyStats = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            return;
        }

        try {
            const response = await fetch(`https://schoolpro-api.uniums.com/api/teacher/attendance/yearly/${currentYear}`, {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            const contentType = response.headers.get("content-type");
            if (!contentType || !contentType.includes("application/json")) {
                throw new Error("Server returned non-JSON response");
            }

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch yearly attendance data");
            }

            const result = await response.json();
            if (!result.success) {
                throw new Error(result.message || "Failed to fetch yearly attendance data");
            }
            setYearStats(result.data);
        } catch (error) {
            console.error("Error fetching yearly stats:", error);
            toast.error(`Error: ${error.message}`);
        }
    };

    useEffect(() => {
        fetchTeacherAttendance();
        if (activeView === 'year' || currentMonth === new Date().getMonth()) {
            fetchYearlyStats();
        }
    }, [currentMonth, currentYear, activeView]);

    const handleMonthChange = (increment) => {
        let newMonth = currentMonth + increment;
        let newYear = currentYear;

        if (newMonth > 11) {
            newMonth = 0;
            newYear += 1;
        } else if (newMonth < 0) {
            newMonth = 11;
            newYear -= 1;
        }

        setCurrentMonth(newMonth);
        setCurrentYear(newYear);
    };

    const getMonthStats = () => {
        const totalDays = getWorkingDaysInMonth(currentYear, currentMonth);
        const presentDays = attendanceData.filter(day => day.status === 'present').length;
        const absentDays = totalDays - presentDays;
        const percentage = totalDays > 0 ? ((presentDays / totalDays) * 100).toFixed(1) : 0;
        return { totalDays, presentDays, absentDays, percentage };
    };

    // Helper function to calculate working days in a month (excluding weekends)
    const getWorkingDaysInMonth = (year, month) => {
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        let workingDays = 0;

        // Example: Define holidays (you can fetch these from an API or database)
        const holidays = [
            new Date(year, month, 15).toDateString(), // Example holiday
            // Add more holidays as needed
        ];

        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const dayOfWeek = date.getDay();
            const isHoliday = holidays.includes(date.toDateString());

            if (dayOfWeek !== 0 && dayOfWeek !== 6 && !isHoliday) {
                workingDays++;
            } else {
                console.log(`Day ${day} is not a working day (weekday: ${dayOfWeek}, holiday: ${isHoliday})`);
            }
        }

        return workingDays;
    };

    const renderCalendar = () => {
        const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
        const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
        const calendarDays = [];
        for (let i = 0; i < firstDayOfMonth; i++) {
            calendarDays.push(
                <div key={`empty-${i}`} className="teacherAttendance-calendar-day empty"></div>
            );
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(currentYear, currentMonth, day);
            const dayOfWeek = date.getDay();
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

            const dayRecord = attendanceData.find(record =>
                new Date(record.date).getDate() === day
            );

            let statusClass = isWeekend ? "weekend" : "";
            let statusIcon = null;
            let statusInfo = null;

            if (dayRecord) {
                if (dayRecord.status === "present") {
                    statusClass = "present";
                    statusIcon = <CheckCircle size={16} />;
                    statusInfo = (
                        <div className="teacherAttendance-time-info">
                            <div>{dayRecord.checkInTime}</div>
                            <div>{dayRecord.checkOutTime}</div>
                        </div>
                    );
                } else if (dayRecord.status === "absent") {
                    statusClass = "absent";
                    statusIcon = <XCircle size={16} />;
                }
            } else if (!isWeekend && new Date() >= date) {
                statusClass = "absent";
                statusIcon = <XCircle size={16} />;
            }

            calendarDays.push(
                <div
                    key={`day-${day}`}
                    className={`teacherAttendance-calendar-day ${statusClass}`}
                >
                    <div className="teacherAttendance-day-header">
                        <span className="teacherAttendance-day-number">{day}</span>
                        {statusIcon && <span className="teacherAttendance-status-icon">{statusIcon}</span>}
                    </div>
                    {statusInfo}
                </div>
            );
        }

        return calendarDays;
    };

    // Render yearly summary
    const renderYearlySummary = () => {
        return yearStats.map((monthData) => (
            <div key={monthData.month} className="teacherAttendance-year-month">
                <div className="teacherAttendance-month-name">{monthData.month}</div>
                <div className="teacherAttendance-month-bar">
                    <div
                        className="teacherAttendance-month-progress"
                        style={{ width: `${monthData.percentage}%` }}
                    ></div>
                </div>
                <div className="teacherAttendance-month-stats">
                    <span>{monthData.presentDays}/{monthData.workingDays} days</span>
                    <span>{monthData.percentage}%</span>
                </div>
            </div>
        ));
    };

    // Calculate overall stats for the year
    const getYearlyStats = () => {
        const totalWorkingDays = yearStats.reduce((sum, month) => sum + parseInt(month.workingDays), 0);
        const totalPresent = yearStats.reduce((sum, month) => sum + parseInt(month.presentDays), 0);
        const yearPercentage = totalWorkingDays > 0 ? ((totalPresent / totalWorkingDays) * 100).toFixed(1) : 0;

        return { totalWorkingDays, totalPresent, yearPercentage };
    };

    const monthStats = getMonthStats();
    const yearlyStats = getYearlyStats();

    return (
        <>
            <div className="teacherAttendance-container">
                <div className="teacherAttendance-header">
                    <div className="teacherAttendance-title">
                        <h2>My Attendance</h2>
                        <div className="teacherAttendance-view-toggle">
                            <button
                                className={`teacherAttendance-toggle-btn ${activeView === 'month' ? 'active' : ''}`}
                                onClick={() => setActiveView('month')}
                            >
                                Month View
                            </button>
                            <button
                                className={`teacherAttendance-toggle-btn ${activeView === 'year' ? 'active' : ''}`}
                                onClick={() => setActiveView('year')}
                            >
                                Year View
                            </button>
                        </div>
                    </div>

                    {activeView === 'month' && (
                        <div className="teacherAttendance-month-selector">
                            <button onClick={() => handleMonthChange(-1)} className="teacherAttendance-nav-btn">
                                <ChevronLeft size={20} />
                            </button>
                            <span className="teacherAttendance-current-month">
                                {months[currentMonth]} {currentYear}
                            </span>
                            <button onClick={() => handleMonthChange(1)} className="teacherAttendance-nav-btn">
                                <ChevronRight size={20} />
                            </button>
                        </div>
                    )}
                </div>

                {isLoading ? (
                    <>
                        <Loader />
                        <div className="teacherAttendance-loading">
                            <div className="teacherAttendance-spinner"></div>
                            <p>Loading attendance data...</p>
                        </div>
                    </>
                ) : error ? (
                    <div className="teacherAttendance-error">
                        <p>{error}</p>
                    </div>
                ) : (
                    <>
                        {activeView === 'month' ? (
                            <>
                                <div className="teacherAttendance-stats-summary">
                                    <div className="teacherAttendance-stat-item">
                                        <div className="teacherAttendance-stat-value">{monthStats.totalDays}</div>
                                        <div className="teacherAttendance-stat-label">Working Days</div>
                                    </div>
                                    <div className="teacherAttendance-stat-item present">
                                        <div className="teacherAttendance-stat-value">{monthStats.presentDays}</div>
                                        <div className="teacherAttendance-stat-label">Present</div>
                                    </div>
                                    <div className="teacherAttendance-stat-item absent">
                                        <div className="teacherAttendance-stat-value">{monthStats.absentDays}</div>
                                        <div className="teacherAttendance-stat-label">Absent</div>
                                    </div>
                                    <div className="teacherAttendance-stat-item percentage">
                                        <div className="teacherAttendance-stat-value">{monthStats.percentage}%</div>
                                        <div className="teacherAttendance-stat-label">Attendance Rate</div>
                                    </div>
                                </div>

                                <div className="teacherAttendance-calendar-container">
                                    <div className="teacherAttendance-calendar-header">
                                        <Calendar size={18} />
                                        <span>Monthly Attendance Calendar</span>
                                    </div>
                                    <div className="teacherAttendance-weekdays">
                                        <div>Sun</div>
                                        <div>Mon</div>
                                        <div>Tue</div>
                                        <div>Wed</div>
                                        <div>Thu</div>
                                        <div>Fri</div>
                                        <div>Sat</div>
                                    </div>
                                    <div className="teacherAttendance-calendar-grid">
                                        {renderCalendar()}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="teacherAttendance-yearly-header">
                                    <h3>{currentYear} Attendance Summary</h3>
                                    <div className="teacherAttendance-yearly-stats">
                                        <div className="teacherAttendance-yearly-stat">
                                            <span className="teacherAttendance-yearly-value">{yearlyStats.totalPresent}/{yearlyStats.totalWorkingDays}</span>
                                            <span className="teacherAttendance-yearly-label">Days Present</span>
                                        </div>
                                        <div className="teacherAttendance-yearly-stat">
                                            <span className="teacherAttendance-yearly-value">{yearlyStats.yearPercentage}%</span>
                                            <span className="teacherAttendance-yearly-label">Overall Rate</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="teacherAttendance-yearly-container">
                                    {renderYearlySummary()}
                                </div>
                            </>
                        )}

                        <div className="teacherAttendance-legend">
                            <div className="teacherAttendance-legend-item">
                                <span className="teacherAttendance-legend-color present"></span>
                                <span>Present</span>
                            </div>
                            <div className="teacherAttendance-legend-item">
                                <span className="teacherAttendance-legend-color absent"></span>
                                <span>Absent</span>
                            </div>
                            <div className="teacherAttendance-legend-item">
                                <span className="teacherAttendance-legend-color weekend"></span>
                                <span>Weekend</span>
                            </div>
                        </div>
                    </>
                )}
            </div>
            <ToastContainer />
        </>

    );
}

export default Attendance;